<template>
  <div class="d-flex flex-column">
    <v-expansion-panels
      v-model="panels"
      multiple
      class="pa-6 d-flex flex-column justify-start flex-grow-1 flex-shrink-0"
    >
      <v-form v-model="isFormValid" ref="checklistForm" style="max-width: 100%">
        <template v-for="(item, index) in items">
          <Separator
            data-testid="separator-response"
            v-if="item.type === 'separator'"
            :key="`${item.id}_separator`"
            :appearancePulseEnabled="appearancePulseEnabled"
            :restoreKey="restoreKey"
            :scoreSummary="sectionScoreSummaries[item.id]"
            :isLoading="isLoading"
            :readOnly="!isEditing"
            :routineIsCompleted="routineIsCompleted"
            :separator="item"
            :imageErrors="imageTypeErrors"
            :requestFinding="allowFindings"
            @addImage="handleImageType"
            @change="handleChange"
            @deleteSignature="handleSignatureDeleted"
            @openSignatureDialog="openSignatureDialog"
            @openCreateFindingModal="showFindingModal"
            @viewFinding="viewFinding"
            @handleWarningContinue="handleWarningContinue"
          />
          <Item
            v-else
            class="flex-grow-1"
            :appearancePulseEnabled="appearancePulseEnabled"
            :data-testid="`checklistReply-item-${item.id}`"
            :restoreKey="restoreKey"
            :imageErrors="imageTypeErrors"
            :item="item"
            :key="item.id"
            :loading="isLoading"
            :readOnly="!isEditing"
            :requestFinding="allowFindings"
            :routineIsCompleted="routineIsCompleted"
            :showDivider="index !== items.length - 1"
            @addImage="handleImageType($event, item.originalIndex)"
            @change="handleChange($event, item.originalIndex)"
            @deleteSignature="handleSignatureDeleted(item.originalIndex)"
            @handleWarningContinue="
              handleWarningContinue($event, item.originalIndex)
            "
            @openCreateFindingModal="showFindingModal(item)"
            @openSignatureDialog="openSignatureDialog(item.originalIndex)"
            @viewFinding="viewFinding(item.originalIndex)"
          />
        </template>
      </v-form>
    </v-expansion-panels>

    <div
      v-if="checklist.items && isEditing"
      class="d-flex mt-0 buttons-container pa-3 flex-grow-0 flex-shrink-1"
      :class="
        checklistResponse && canCancel ? 'justify-space-between' : 'justify-end'
      "
    >
      <Button
        v-if="isEditing && checklistResponse && canCancel"
        plain
        color="grey"
        :text="$t('common.button.cancel')"
        @click="handleCancelEdit"
      />

      <div class="d-flex">
        <Button
          v-if="canSave"
          plain
          color="primary"
          class="mr-3"
          :loading="isLoading"
          :text="$t('common.button.save')"
          @click="saveResponses(false)"
        />

        <Button
          v-if="canComplete"
          color="primary"
          icon="mdi-text-box-check-outline"
          :loading="isLoading"
          :text="$t('home.to_do.complete_action')"
          @click="saveResponses(true)"
        />
      </div>
    </div>
    <AddFindingModal
      v-if="showCreateFindingModal"
      :routine="routine"
      :routineDefinition="routineDefinition"
      :show="showCreateFindingModal"
      @close="closeCreateFindingModal"
      @created="handleFindingCreated"
    />

    <div v-if="showSignatureDialog">
      <SignatureDialog
        :title="$t('checklist.signature_dialog_title')"
        :subtitle="$t('checklist.signature_dialog_subtitle')"
        :signerName="currentSignerName"
        data-testid="checklistReply-dialog-signature"
        @close="closeSignatureDialog"
        @save="handleSignatureSaved($event, selectedSignatureItemIndex)"
      />
    </div>

    <ConfirmationModal
      modalTestID="checklistReply-modal-resetConfirmation"
      confirmTestID="checklistReply-button-confirmReset"
      cancelTestID="checklistReply-button-cancelReset"
      :show="showResetConfirmation"
      :text="resetConfirmationText"
      @cancel="handleResetConfirmationCancel"
      @close="handleResetConfirmationCancel"
      @confirm="handleResetConfirmationConfirm"
    />

    <FindingDrawer
      v-if="openAddressFindingDrawer"
      :visible="openAddressFindingDrawer"
      :findingId="viewFindingData.findingId"
      @close="onAddressFindingDrawerClose"
      @deleted="deleteCloseUpdateDrawer()"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'
import api from '@/api'
import { utils } from 'frontend-js-shared'

import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import { normalizeChecklistItems } from './signatureUtils'
import { buildSectionScoreSummary } from './scoringDisplay'
import { hydrateChecklistItems } from './checklistHydrationUtils'
import { buildRenderItems } from './checklistGrouping'
import {
  findNewlyHiddenItemIds,
  getDependentsToReset,
  resetNewlyHiddenAnswers
} from './checklistVisibilityDiff'
import Separator from './Separator.vue'
import Item from './Item.vue'
import SignatureDialog from './SignatureDialog.vue'
import Button from '@/components/UI/Button'
import ConfirmationModal from '@/components/Modals/ConfirmationModal.vue'
import AddFindingModal from '@/containers/RoutineExecutionView/AddFindingModal.vue'

import FindingDrawer from '@/components/FindingDrawer/FindingDrawer.vue'
import { ITEM_TYPES } from '@/components/Checklists/ChecklistCreation/constants'

export default {
  name: 'ChecklistReply',

  mixins: [mixin, trackerMixin],

  components: {
    Item,
    SignatureDialog,
    Separator,
    Button,
    ConfirmationModal,
    AddFindingModal,
    FindingDrawer,
  },

  props: {
    checklistDefinition: Object,
    checklistResponse: Object,
    isLoading: Boolean,
    hasChecklistChanged: Boolean,
    routine: Object,
    routineDefinition: {
      type: Object,
      required: true,
    },
    isEditing: Boolean,
    isCollapsedAll: Boolean,
    canSave: Boolean,
    canComplete: Boolean,
    canCancel: Boolean,
    validateAnswers: Boolean,
  },

  data() {
    return {
      checklist: this.checklistResponse
        ? _.cloneDeep(this.checklistResponse)
        : _.cloneDeep(this.checklistDefinition),
      isFormValid: false,
      imageTypeErrors: [],
      panels: [],
      showCreateFindingModal: false,
      showSignatureDialog: false,
      selectedSignatureItemIndex: null,
      openAddressFindingDrawer: false,
      viewFindingData: {
        findingId: '',
        questionOriginalIndex: '',
      },
      selectedItemIndex: '',
      // Set once the feature flags resolve; the items computed reads it
      // reactively so rendering updates when it flips.
      conditionalLogicEnabled: false,
      // Flips after the first render settles. Items mounting before it are
      // simply there from the start and must not pulse; items mounting after
      // it are appearances and do.
      initialRenderComplete: false,
      // Sections the user expanded, tracked by id so hiding a section does
      // not shift the expansion onto another one.
      expandedSectionIds: [],
      // Pending trigger change while the user confirms hiding answered
      // dependents; it applies only on confirmation.
      showResetConfirmation: false,
      pendingTriggerChange: null,
      // Bumped when a trigger change is cancelled; stateful trigger forms
      // re-render from the stored answers.
      restoreKey: 0,
    }
  },

  async created() {
    this._previousVisibilityById = null
    this.checklist = this.normalizeChecklist(this.checklist)
    this.hydrateSignatureSignerNames()

    await this.$onFeatureFlagsReady()

    this.conditionalLogicEnabled = this.isConditionalLogicInRoutinesEnabled()
  },

  mounted() {
    this.$nextTick(() => {
      this.initialRenderComplete = true
    })
  },

  computed: {
    ...mapGetters(['organizationId', 'account']),

    currentSignerName() {
      return [this.account?.displayname, this.account?.familyname]
        .filter(Boolean)
        .join(' ')
        .trim()
    },

    allowFindings() {
      return !_.isEmpty(this.routine)
    },

    checklistVisibility() {
      if (!this.conditionalLogicEnabled) {
        return { visibilityById: new Map(), dependentsByTriggerId: new Map() }
      }

      return utils.checklists.computeChecklistVisibility(
        this.checklist?.items ?? []
      )
    },

    visibilityById() {
      return this.checklistVisibility.visibilityById
    },

    dependentsByTriggerId() {
      return this.checklistVisibility.dependentsByTriggerId
    },

    hasLinkedFindings() {
      const pending = this.pendingTriggerChange
      if (!pending) return false

      const triggerItem = this.checklist.items[pending.index]

      return (
        !_.isEmpty(triggerItem?.responseFindings) ||
        pending.dependentsToReset.some(
          dependent => !_.isEmpty(dependent.responseFindings)
        )
      )
    },

    resetConfirmationText() {
      const messageKey = this.hasLinkedFindings
        ? 'checklist.reset_conditional_answers_findings_message'
        : 'checklist.reset_conditional_answers_message'

      return this.$t(messageKey)
    },

    renderedSeparators() {
      return this.items.filter(item => item?.type === ITEM_TYPES.SEPARATOR)
    },

    appearancePulseEnabled() {
      return (
        this.initialRenderComplete &&
        this.conditionalLogicEnabled &&
        this.isEditing
      )
    },

    // Unfiltered render items: unlike `items`, visibility is not applied —
    // sectionScoreSummaries scores sections over their full membership so a
    // hidden scored child keeps its contribution to the saved score.
    scoringItems() {
      return buildRenderItems(this.checklist?.items ?? [], {
        allowFindings: this.allowFindings,
      })
    },

    sectionScoreSummaries() {
      const summaries = {}

      this.scoringItems.forEach(item => {
        if (item?.type !== ITEM_TYPES.SEPARATOR) return

        summaries[item.id] = buildSectionScoreSummary(item)
      })

      return summaries
    },

    items() {
      const sourceItems = this.checklist?.items ?? []

      if (!this.conditionalLogicEnabled) {
        return buildRenderItems(sourceItems, {
          allowFindings: this.allowFindings,
        })
      }

      return buildRenderItems(sourceItems, {
        visibilityById: this.visibilityById,
        allowFindings: this.allowFindings,
      })
    },

    flatItems() {
      const nonGroupedItems = this.items.filter(
        item => item.type !== 'separator'
      )
      const separatorItems = _.flatten(
        this.items
          .filter(item => item.type === 'separator')
          .map(mapItem => mapItem.items)
      )
      return [...nonGroupedItems, ...separatorItems]
    },
    routineIsCompleted() {
      return this.routine?.status === 'completed'
    },
  },

  watch: {
    visibilityById: 'resetHiddenAnswers',
    panels: 'trackExpandedSections',
    renderedSeparators: 'remapExpandedPanels',
    isCollapsedAll(value) {
      this.collapseAll(value)
    },
    // Entering edit mode seeds the snapshot so the first hide already
    // resets; data replacements re-seed from scratch.
    isEditing(value) {
      this._previousVisibilityById =
        this.conditionalLogicEnabled && value ? this.visibilityById : null
    },
    checklistDefinition() {
      this._previousVisibilityById = null
      this.checklist = this.normalizeChecklist(
        this.checklistResponse
          ? _.cloneDeep(this.checklistResponse)
          : _.cloneDeep(this.checklistDefinition)
      )
      this.hydrateSignatureSignerNames()
    },
    checklistResponse() {
      this._previousVisibilityById = null
      this.checklist = this.normalizeChecklist(
        this.checklistResponse
          ? _.cloneDeep(this.checklistResponse)
          : _.cloneDeep(this.checklistDefinition)
      )
      this.hydrateSignatureSignerNames()
    },
  },

  methods: {
    isConditionalLogicInRoutinesEnabled() {
      return this.$isFeatureEnabled(
        this.$featuresKeys?.conditionalLogicInRoutines
      )
    },

    // Reset answers of items that just became hidden; the first evaluation
    // only seeds the snapshot — already-hidden answers are cleared at save.
    resetHiddenAnswers() {
      if (!this.conditionalLogicEnabled || !this.isEditing) {
        this._previousVisibilityById = null
        return
      }

      const previousVisibilityById = this._previousVisibilityById
      this._previousVisibilityById = this.visibilityById

      if (!previousVisibilityById) return

      const newlyHiddenIds = findNewlyHiddenItemIds({
        previousVisibilityById,
        currentVisibilityById: this.visibilityById,
      })
      if (_.isEmpty(newlyHiddenIds)) return

      this.checklist.items = resetNewlyHiddenAnswers(
        this.checklist.items,
        newlyHiddenIds
      )
      this.$emit('checklistHasChanged', true)
    },

    trackExpandedSections() {
      this.expandedSectionIds = this.panels
        .map(index => this.renderedSeparators[index]?.id)
        .filter(Boolean)
    },

    remapExpandedPanels() {
      this.panels = this.expandedSectionIds
        .map(sectionId =>
          this.renderedSeparators.findIndex(sep => sep.id === sectionId)
        )
        .filter(index => index >= 0)
    },

    normalizeChecklist(checklist) {
      if (!checklist?.items) return checklist

      return {
        ...checklist,
        items: normalizeChecklistItems(checklist.items),
      }
    },

    async hydrateSignatureSignerNames() {
      const currentItems = this.checklist?.items ?? []
      const { items, status } = await hydrateChecklistItems(
        currentItems,
        async idsToResolve => {
          const response = await api.account.getAccountsByIds(
            this.organizationId,
            {
              filters: { id: idsToResolve },
              limit: idsToResolve.length,
            }
          )

          return response?.data?.results || []
        }
      )

      if (status === 'fallback') {
        this.showError(this.$t('checklist.signature_signer_hydration_failed'))
      }

      let hydratedItemIndex = 0

      this.checklist = {
        ...this.checklist,
        items: currentItems.map(item => {
          const hydratedItem = items[hydratedItemIndex] ?? item
          hydratedItemIndex += 1

          return hydratedItem
        }),
      }
    },

    validateImageType() {
      const errors = this.flatItems
        .filter(item => {
          if (!item.isRequired) return false
          if (item.type === ITEM_TYPES.IMAGE) {
            return _.isEmpty(item.responseImages)
          }
          if (item.type === ITEM_TYPES.SIGNATURE) {
            return !item?.signature
          }
          return false
        })
        .map(item => item.id)
      this.imageTypeErrors = errors
      return errors
    },

    hasMissingFindings() {
      return this.flatItems
        .filter(item => item.hasOptionRequiringFinding)
        .map(filteredItem => {
          const response = _.isArray(filteredItem.response)
            ? filteredItem.response
            : [filteredItem.response]

          return filteredItem.options.some(
            option =>
              option.requiresFinding &&
              response.includes(option.id) &&
              _.isEmpty(filteredItem.responseFindings)
          )
        })
        .some(result => result)
    },

    async saveResponses(shouldComplete) {
      if (shouldComplete || this.validateAnswers) {
        this.$refs.checklistForm.validate()
        const imageErrors = this.validateImageType()
        if (!this.isFormValid || !_.isEmpty(imageErrors)) {
          this.collapseAll(true)
          return this.showError(
            this.$t('notifications.required_questions_missing')
          )
        }

        if (this.allowFindings && this.hasMissingFindings()) {
          this.collapseAll(true)
          return this.showError(
            this.$t('notifications.required_findings_missing')
          )
        }
      }

      this.$emit('save', {
        updating: Boolean(this.checklistResponse),
        validated: shouldComplete,
        checklist: {
          ...this.checklist,
          items: this.checklist.items?.map?.(item =>
            this.buildSaveItem(item)
          ),
        },
      })
    },

    // Hidden items submit the cleared shape unconditionally — whether just
    // reset in memory or already hidden at load. Flag off clears nothing.
    buildSaveItem(item) {
      const isHidden =
        this.conditionalLogicEnabled &&
        this.visibilityById.get(utils.getEntityId(item)) === false

      return this.getMediaIds(
        isHidden ? utils.checklists.clearItemAnswers(item) : item
      )
    },

    getMediaIds(item) {
      if (item?.type === ITEM_TYPES.SIGNATURE) {
        const responseImages = item?.signature?.mediaId
          ? [item.signature.mediaId]
          : []

        return {
          ..._.omit(item, 'signature'),
          response: null,
          responseImages,
          signer: item?.signature?.signerId ?? null,
        }
      }

      return {
        ...item,
        responseImages: item?.responseImages?.map?.(media => media.id),
      }
    },

    checkIfFindingIsMissing(ids) {
      return this.checklist.items.filter(
        item => ids.includes(item.id) && _.isEmpty(item.responseFindings)
      )
    },

    handleChange(value, index) {
      this.requestTriggerChange(value, index)
    },

    handleWarningContinue(value, index) {
      this.requestTriggerChange(value, index, { clearFindings: true })
    },

    // Applies a trigger change only after checking whether answered
    // dependents would be hidden; otherwise the data-loss confirmation
    // decides.
    requestTriggerChange(value, index, { clearFindings = false } = {}) {
      const dependentsToReset = getDependentsToReset({
        items: this.checklist.items,
        triggerItem: this.checklist.items[index],
        nextResponse: value,
        dependentsByTriggerId: this.dependentsByTriggerId,
        visibilityById: this.visibilityById,
      })

      if (!_.isEmpty(dependentsToReset)) {
        this.pendingTriggerChange = {
          value,
          index,
          dependentsToReset,
          clearFindings,
        }
        this.showResetConfirmation = true
        return
      }

      this.applyTriggerChange(value, index, { clearFindings })
    },

    applyTriggerChange(value, index, { clearFindings = false } = {}) {
      const item = this.checklist.items[index]
      const newItems = [...this.checklist.items]

      newItems[index] = {
        ...item,
        response: value,
        ...(clearFindings ? { responseFindings: [] } : {}),
      }
      this.checklist.items = newItems
      if (!this.hasChecklistChanged && value === 0) return
      this.$emit('checklistHasChanged', true)
    },

    handleResetConfirmationConfirm() {
      const pending = this.pendingTriggerChange

      this.closeResetConfirmation()

      if (!pending) return

      this.applyTriggerChange(pending.value, pending.index, {
        clearFindings: pending.clearFindings,
      })
    },

    handleResetConfirmationCancel() {
      this.restoreKey += 1
      this.closeResetConfirmation()
    },

    closeResetConfirmation() {
      this.showResetConfirmation = false
      this.pendingTriggerChange = null
    },

    removeError(id) {
      const removeError = this.imageTypeErrors.filter(item => item !== id)
      this.imageTypeErrors = removeError
    },

    handleImageType(value, index) {
      const isImageRequired = this.checklist.items[index].isRequired
      if (isImageRequired) {
        if (_.isEmpty(value)) {
          this.imageTypeErrors.push(this.checklist.items[index].id)
        } else {
          this.removeError(this.checklist.items[index].id)
        }
      }
      this.checklist.items[index].responseImages = value
      this.$emit('checklistHasChanged', true)
    },

    openSignatureDialog(index) {
      this.selectedSignatureItemIndex = index
      this.showSignatureDialog = true
    },

    closeSignatureDialog() {
      this.showSignatureDialog = false
      this.selectedSignatureItemIndex = null
    },

    handleSignatureSaved(signature, index) {
      const newItems = [...this.checklist.items]

      newItems[index] = {
        ...newItems[index],
        response: null,
        signature,
        signer: this.account,
      }

      this.checklist.items = newItems
      this.closeSignatureDialog()
      this.$emit('checklistHasChanged', true)
    },

    handleSignatureDeleted(index) {
      const newItems = [...this.checklist.items]

      newItems[index] = {
        ...newItems[index],
        response: null,
        signature: null,
      }

      this.checklist.items = newItems
      this.$emit('checklistHasChanged', true)
    },

    collapseAll(value) {
      const separators = this.items
        .filter(item => item.type === 'separator')
        .map((item, index) => index)

      this.panels = value ? separators : []
    },

    handleFindingCreated(finding) {
      const item = this.checklist.items[this.selectedItemIndex]
      const response = _.isArray(item.response)
        ? item.response
        : [item.response]

      const newItems = [...this.checklist.items]

      newItems[this.selectedItemIndex] = {
        ...item,
        responseFindings: response.map(responseValue => ({
          response: responseValue,
          finding: finding._id,
        })),
      }

      this.checklist.items = newItems
      this.showCreateFindingModal = false
      this.selectedItemIndex = null
      this.$emit('getFindings', { page: 1 })
    },

    showFindingModal(item) {
      this.selectedItemIndex = item.originalIndex
      this.showCreateFindingModal = !this.showCreateFindingModal
    },

    closeCreateFindingModal() {
      this.showCreateFindingModal = false
    },

    viewFinding(questionIndex) {
      const item = this.checklist.items[questionIndex]

      this.viewFindingData.findingId = item.responseFindings[0]?.finding
      this.viewFindingData.questionOriginalIndex = questionIndex
      this.openAddressFindingDrawer = true
    },

    onAddressFindingDrawerClose() {
      this.viewFindingData.findingId = ''
      this.viewFindingData.questionOriginalIndex = ''
      this.openAddressFindingDrawer = false
    },

    deleteCloseUpdateDrawer() {
      this.checklist.items[
        this.viewFindingData.questionOriginalIndex
      ].responseFindings = []

      this.viewFindingData.findingId = ''
      this.viewFindingData.questionOriginalIndex = ''

      this.openAddressFindingDrawer = false
      this.$emit('getFindings', { page: 1 })
    },

    async handleCancelEdit() {
      if (this.hasChecklistChanged) {
        if (window.confirm(this.$t('checklist.cancel_edit_confirmation'))) {
          this.$emit('checklistHasChanged', false)
        }
      }
      this.$emit('isEditing', false)
    },
  },
}
</script>

<style lang="scss" scoped>
.buttons-container {
  position: sticky;
  bottom: 0;
  z-index: 1;
  background-color: $color-white;
  border-top: 1px solid $color-blue-grey-lighten2;
}
</style>
